/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {safeJSONStringify} from './JSONUtils';

/**
 * Provides a utility which returns an approximation of the given value's
 * size in bytes.
 *
 * By default, this utility assumes UTF-8 encoding, and first serializes
 * the object into a JSON string, after which each character is iterated,
 * and the byte size of each character is determined based on it's UTF-8
 * encoded length. The total size is calculated by summing the byte size
 * of each character. This method provides an approximation and may not
 * accurately reflect the actual memory consumption of the object.
 *
 * The size calculation is based on the following UTF-8 encoding rules:
 *  - If the code point is less than 0x80 (128 in decimal), it's a single
 *    byte character in UTF-8,  and the is incremented by 1.
 *  - If the code point is between 0x80 and 0x800 (128 to 2048 in decimal),
 *    it's a two-byte character, and the size is incremented by 2.
 *  - If the code point is between 0x800 - 0x10000 (2048 to 65536 in decimal),
 *    it's a three-byte character, and the size is incremented by 3.
 *  - For code points greater than or equal to 0x10000, these are four-byte
 *    characters in UTF-8, and the size is incremented by 4.
 *
 * ```typescript
 *   import {sizeOf} from '@ion/core/src/utils/MemoryUtils';
 *   ...
 *
 *   // calculate the size of an object ...
 *   if (sizeOf({...}) > 512) {
 *     ...
 *   }
 *
 * ```
 *
 * @param   object A non-primitive type (Object or Array) for which an
 *          approximation of the object's size in bytes is to be determined.
 *          The object is serialized to a JSON string for size calculation.
 * @returns An approximation of the object's size in bytes, based on its
 *          UTF-8 encoded string representation.
 */
export const sizeOf = (value: unknown): number => {
  const stringified = safeJSONStringify(value);
  const length = stringified?.length;
  let size = 0;
  for (let i = 0; i < length; i++) {
    const char: number = stringified.charCodeAt(i);
    if (char < 0x80) {
      size += 1;
    } else if (char < 0x800) {
      size += 2;
    } else if (char < 0x10000) {
      size += 3;
    } else {
      size += 4;
    }
  }
  return size;
};
